import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createCoupon,
  fetchCoupons,
  updateCoupon,
  deleteCoupon,
  toggleCouponStatus,
  resetCouponStatus,
} from "../../../slice/coupons/couponSlice";

const emptyForm = {
  code:           "",
  discountAmount: "",
};

export default function CouponList() {
  const dispatch = useDispatch();
  const { list = [], loading, success, error } = useSelector((s) => s.coupon);

  const [showModal,       setShowModal]       = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editId,          setEditId]          = useState(null);
  const [deleteId,        setDeleteId]        = useState(null);
  const [formData,        setFormData]        = useState(emptyForm);

  useEffect(() => { dispatch(fetchCoupons()); }, [dispatch]);

  useEffect(() => {
    if (success) {
      setShowModal(false);
      setEditId(null);
      setFormData(emptyForm);
      dispatch(resetCouponStatus());
    }
  }, [success, dispatch]);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const openAdd = () => {
    setEditId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (coupon) => {
    setEditId(coupon._id);
    setFormData({
      code:           coupon.code,
      discountAmount: coupon.discountAmount,
    });
    setShowModal(true);
  };

  const handleSubmit = () => {
    const payload = {
      code:           formData.code,
      discountAmount: Number(formData.discountAmount),
    };
    if (editId) dispatch(updateCoupon({ id: editId, data: payload }));
    else        dispatch(createCoupon(payload));
  };

  const confirmDelete = () => {
    dispatch(deleteCoupon(deleteId));
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const closeAll = () => {
    setShowModal(false);
    setShowDeleteModal(false);
    setEditId(null);
    setDeleteId(null);
  };

  return (
    <div className="row">
      <div className="card mt-3">
      {/* ── HEADER ── */}
      <div className="card-header d-flex justify-content-between align-items-center">
        <h2 className="user-list-title">Coupon List</h2>
        <button className="btn cusBTN" onClick={openAdd}>
          Add Coupon
        </button>
      </div>

      {/* ── TABLE ── */}
      <div className="table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>SI</th>
              <th>Code</th>
              <th>Discount Amount</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {list.length > 0 ? (
              list.map((coupon, index) => (
                <tr key={coupon._id}>
                  <td>{index + 1}</td>

                  {/* Code badge */}
                  <td>
                    <span style={{
                      fontFamily: "monospace", fontWeight: 700, fontSize: 13,
                      background: "#fff7ed", color: "#c2410c",
                      padding: "3px 10px", borderRadius: 20,
                      border: "1px solid #fed7aa", letterSpacing: "0.05em"
                    }}>
                      {coupon.code}
                    </span>
                  </td>

                  {/* Discount Amount */}
                  <td style={{ fontWeight: 600, color: "#16a34a" }}>
                    ₹{coupon.discountAmount}
                  </td>

                  {/* Status toggle */}
                  <td>
                    <div
                      onClick={() => dispatch(toggleCouponStatus(coupon._id))}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        cursor: "pointer", userSelect: "none",
                        padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: coupon.status ? "#dcfce7" : "#fee2e2",
                        color:      coupon.status ? "#16a34a"  : "#dc2626",
                        border: `1px solid ${coupon.status ? "#86efac" : "#fca5a5"}`,
                        transition: "all 0.2s",
                      }}
                    >
                      {/* Toggle track */}
                      <div style={{
                        width: 28, height: 15, borderRadius: 8,
                        background: coupon.status ? "#22c55e" : "#d1d5db",
                        position: "relative", transition: "background 0.25s", flexShrink: 0,
                      }}>
                        <div style={{
                          width: 11, height: 11, borderRadius: "50%", background: "#fff",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          position: "absolute", top: 2,
                          left: coupon.status ? 15 : 2,
                          transition: "left 0.25s",
                        }} />
                      </div>
                      {coupon.status ? "Enabled" : "Disabled"}
                    </div>
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="d-flex gap-2">
                      <div className="btn btn-soft-primary btn-sm" onClick={() => openEdit(coupon)}>
                        <iconify-icon icon="solar:pen-2-broken" />
                      </div>
                      <div
                        className="btn btn-soft-danger btn-sm"
                        onClick={() => { setDeleteId(coupon._id); setShowDeleteModal(true); }}
                      >
                        <iconify-icon icon="solar:trash-bin-minimalistic-2-broken" />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">No coupons found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ══════════════════════════════════
          DELETE CONFIRMATION MODAL
      ══════════════════════════════════ */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeAll}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body text-center">
              Are you sure you want to delete this coupon?
            </div>
            <div className="modal-footer">
              <button className="btn cusClose"  onClick={closeAll}>Cancel</button>
              <button className="btn cuscancel" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════
          CREATE / EDIT MODAL
      ══════════════════════════════════ */}
      {showModal && (
        <div className="modal-overlay" onClick={closeAll}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 400, borderRadius: 12, padding: 0, overflow: "hidden" }}
          >
            {/* Header */}
            <div style={{
              padding: "16px 20px", background: "#f8f9fa",
              borderBottom: "1px solid #e5e7eb",
              display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                {editId ? "Edit Coupon" : "Create Coupon"}
              </h3>
              <button onClick={closeAll} style={{ border: "none", background: "transparent", fontSize: 22, cursor: "pointer", color: "#6b7280", lineHeight: 1 }}>
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 18 }}>

              {/* Coupon Code */}
              <div>
                <label className="form-label">
                  Coupon Code <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g. SAVE20"
                  style={{ width: "100%", textTransform: "uppercase" }}
                />
              </div>

              {/* Discount Amount */}
              <div>
                <label className="form-label">
                  Discount Amount <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <span style={{
                    position: "absolute", left: 10, top: "50%",
                    transform: "translateY(-50%)", color: "#9ca3af", fontSize: 13
                  }}>
                    ₹
                  </span>
                  <input
                    type="number"
                    name="discountAmount"
                    value={formData.discountAmount}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="0"
                    min="0"
                    style={{ width: "100%", paddingLeft: 26 }}
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <div style={{
                  background: "#fee2e2", color: "#991b1b",
                  padding: "8px 10px", borderRadius: 6, fontSize: 14
                }}>
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: "14px 20px", borderTop: "1px solid #e5e7eb",
              display: "flex", justifyContent: "flex-end", gap: 10, background: "#f9fafb"
            }}>
              <button
                onClick={closeAll}
                style={{
                  padding: "8px 16px", borderRadius: 6,
                  border: "1px solid #d1d5db", background: "#fff", cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  padding: "8px 22px", borderRadius: 6, border: "none",
                  background: "#f97316", color: "#fff", fontWeight: 500,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? "Saving..." : editId ? "Update Coupon" : "Create Coupon"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}